import { useEffect, useState } from "react";
import { makeRequest } from "../utils/request";

export interface STTModelCapabilities {
  languages: string[];
  supportsNoiseReduction: boolean;
  hasBuiltinVAD: boolean;
  supportsEndpointing: boolean;
  defaultEndpointing?: number;
  supportsPrompt: boolean;
  promptType?: "keywords" | "system" | "none";
  supportsAutoSwitch: boolean;
  supportsPrewarm: boolean;
  supportsFilter: boolean;
  supportsEOT: boolean;
  defaultEOTThreshold?: number;
  defaultEOTTimeoutMs?: number;
  supportsEagerEOT: boolean;
  defaultEagerEOTThreshold?: number;
  supportsVADMode: boolean;
  defaultVADMode?: "server_vad" | "semantic_vad";
  supportsVADEagerness: boolean;
  defaultVADEagerness?: "auto" | "low" | "medium" | "high";
}

export interface STTModel {
  value: string;
  label: string;
  capabilities: STTModelCapabilities;
}

export interface STTProvider {
  id: string;
  name: string;
  models: STTModel[];
}

interface ProvidersResponse {
  providers: STTProvider[];
}

export const useSTTProviders = () => {
  const [providers, setProviders] = useState<STTProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const data = await makeRequest<ProvidersResponse>("/v1/stt/providers");
        setProviders(data.providers);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch STT providers");
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  const getAllModels = (): STTModel[] => {
    return providers.flatMap((provider) => provider.models);
  };

  const getModelByValue = (value: string): STTModel | undefined => {
    return getAllModels().find((model) => model.value === value);
  };

  const getProviderForModel = (modelValue: string): STTProvider | undefined => {
    return providers.find((provider) => provider.models.some((model) => model.value === modelValue));
  };

  return {
    providers,
    loading,
    error,
    getAllModels,
    getModelByValue,
    getProviderForModel,
  };
};
